import QuantityToggler from '@/components/Cart/QuantityToggler/QuantityToggler'
import UpdatedAtTooltip from '@/components/Feed/UpdatedAtTooltip/UpdatedAtTooltip'
import { ORDER_PRODUCT_STATUSES } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { OrderProductModel } from '@/types'
import { formatPriceWithVat } from '@/utils/price/vat'
import getProductKey from '@/utils/products/getProductKey'

import cn from './OrderProductCard.module.scss'

type OrderProductCardProps = {
  product: OrderProductModel
  orderCode: number
  duplicateOrderProductsQuantities: { [x: string]: number }
  canEditProducts: boolean
  handleDeleteOrderProduct: (product: OrderProductModel) => void
}

const OrderProductCard = ({
  product,
  orderCode,
  duplicateOrderProductsQuantities,
  canEditProducts,
  handleDeleteOrderProduct,
}: OrderProductCardProps) => {
  const productKey = String(getProductKey(product))
  const quantity =
    duplicateOrderProductsQuantities[productKey] ?? product.quantity ?? 0

  const price = product.price ?? 0
  const retailPrice = product.retailPrice ?? 0

  const optPrice = formatPriceWithVat(price)
  const optTotal = formatPriceWithVat(quantity * price)
  const rrcPrice = formatPriceWithVat(retailPrice)
  const rrcTotal = formatPriceWithVat(quantity * retailPrice)

  const { selectedOrganisation } = useUserInfoStore((state) => state)
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0

  const statusText = ORDER_PRODUCT_STATUSES[product.status] || product.status
  const isRejected = product.status === 'REJECTED'
  const isReceived = product.status === 'RECEIVED'
  const isReadyForPickup = product.status === 'READY_FOR_PICKUP'

  const isLowStock =
    product.availableQuantity !== undefined &&
    product.availableQuantity < product.quantity

  return (
    <div
      className={[
        cn.card,
        isOrgSelected ? cn.organisationSelected : '',
        isLowStock ? cn.lowStock : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={cn.header}>
        <div className={cn.titleBlock}>
          <span className={cn.article}>{product.nomenclatureDto.article}</span>
          {product.nomenclatureDto.brandDto?.description && (
            <span className={cn.brand}>
              {product.nomenclatureDto.brandDto.description}
            </span>
          )}
        </div>
        <span
          className={[
            cn.statusBadge,
            isRejected ? cn.statusRejected : '',
            isReceived ? cn.statusReceived : '',
            isReadyForPickup ? cn.statusReadyForPickup : '',
          ].join(' ')}
        >
          {statusText}
        </span>
      </div>

      <p className={cn.name}>{product.nomenclatureDto.description}</p>

      <div className={cn.infoGrid}>
        <div className={cn.infoItem}>
          <span className={cn.infoLabel}>Количество</span>
          <div className={cn.quantityControl}>
            {canEditProducts ? (
              <QuantityToggler
                location="profile/orders/:code"
                productQuantity={quantity}
                element={product}
                orderCode={orderCode}
                isShowWarning={true}
                itemDesc={
                  product.nomenclatureDto.unitOfMeasurementDto?.description ||
                  'шт'
                }
              />
            ) : (
              <span className={cn.infoValue}>{quantity}</span>
            )}
          </div>
        </div>
        <div className={cn.infoItem}>
          <span className={cn.infoLabel}>Сегмент</span>
          <span className={cn.infoValue}>
            {product.segmentDto?.description || '—'}
          </span>
        </div>
        <div className={cn.infoItem}>
          <span className={cn.infoLabel}>Склад</span>
          <span className={cn.infoValue}>
            {product.storeShortModel?.description || '—'}
          </span>
        </div>
        {isLowStock && (
          <div className={cn.infoItem}>
            <span className={`${cn.infoLabel} ${cn.lowStockText}`}>
              Доступно на складе
            </span>
            <span
              className={`${cn.infoValue} ${cn.lowStockText} ${cn.lowStockValue}`}
            >
              {product.availableQuantity}
            </span>
          </div>
        )}
      </div>

      <div className={cn.priceBlock}>
        <div className={cn.priceCol}>
          <span className={cn.priceLabel}>ОПТ цена</span>
          <span className={cn.priceValue}>
            <UpdatedAtTooltip
              updatedAt={product.priceUpdatedAt}
              kind="price"
              priceSource={product.priceSource}
            >
              {optPrice} BYN
            </UpdatedAtTooltip>
          </span>
        </div>
        <div className={cn.priceDivider} />
        <div className={cn.priceCol}>
          <span className={cn.priceLabel}>ОПТ сумма</span>
          <span className={cn.priceValue}>{optTotal} BYN</span>
        </div>
        <div className={cn.priceDivider} />
        <div className={cn.priceCol}>
          <span className={cn.priceLabel}>РРЦ цена</span>
          <span className={cn.priceValue}>{rrcPrice} BYN</span>
        </div>
        <div className={cn.priceDivider} />
        <div className={cn.priceCol}>
          <span className={cn.priceLabel}>РРЦ сумма</span>
          <span className={cn.priceValue}>{rrcTotal} BYN</span>
        </div>
      </div>

      <div className={cn.total}>
        <span className={cn.totalLabel}>Итого с НДС</span>
        <span className={cn.totalValue}>{optTotal} BYN</span>
      </div>

      {canEditProducts && (
        <div className={cn.delete}>
          <button
            className={[
              cn.deleteBtn,
              isOrgSelected ? cn.deleteBtnOrg : '',
            ].join(' ')}
            onClick={() => handleDeleteOrderProduct(product)}
          >
            Удалить
          </button>
        </div>
      )}
    </div>
  )
}

export default OrderProductCard
